import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import debug from 'debug';
import { z } from 'zod';

import {
  EnvironmentDeploymentType,
  SearchFieldType,
  SearchPlatform,
  WarehouseEntityType,
} from '@/__generated__/graphql';
import {
  accessTypes,
  filterMaterializationTypes,
  modelingLayerTypes,
} from '@/typings/dbt';
import { unique } from '@/util/array';
import { zResourceTypeExplorer } from '@/util/resourceType';
import { useDbtCloudAccount } from './DbtCloudProvider';

export const zAccessTypes = z.enum(accessTypes);
export const zMaterializationTypes = z.enum(filterMaterializationTypes);
export const zModelingLayerTypes = z.enum(modelingLayerTypes);
/**
 * Supported search field types that are exposed in the UI filter panel.
 * This should match the SELECTION_LIST in AdvancedFilters.tsx.
 * Note: ColumnDescription is intentionally excluded as it's not in the UI.
 */
export const allSearchTypes = [
  SearchFieldType.Name,
  SearchFieldType.Column,
  SearchFieldType.Relation,
  SearchFieldType.Description,
  SearchFieldType.Code,
];
export const zSearchTypes = z.nativeEnum(SearchFieldType);
export const zSearchPlatform = z.nativeEnum(SearchPlatform);
export const zEnvironmentDeploymentType = z.nativeEnum(EnvironmentDeploymentType);
export const zWarehouseAssetType = z.nativeEnum(WarehouseEntityType);

export const zNodeFilters = z.object({
  column: z.boolean().optional(),
  resourceType: z.array(zResourceTypeExplorer).optional(),
  access: z.array(zAccessTypes).optional(),
  tags: z.array(z.string()).optional(),
  materialization: z.array(zMaterializationTypes).optional(),
  modelingLayer: z.array(zModelingLayerTypes).optional(),
  searchFields: z.array(zSearchTypes).optional(),
  platforms: z.array(zSearchPlatform).optional(),
  projects: z.array(z.number()).optional(),
  environmentTypes: z.array(zEnvironmentDeploymentType).optional(),
  warehouseAssetTypes: z.array(zWarehouseAssetType).optional(),
});

export const zNodeFilterSerialized = zNodeFilters.transform((obj) => {
  return JSON.stringify(obj);
});

export type NodeFilters = z.infer<typeof zNodeFilters>;

export const getDefaultAccountFilters = (hasCatalogIngestion: boolean): NodeFilters => {
  return {
    platforms: [
      SearchPlatform.Dbt,
      ...(hasCatalogIngestion ? [SearchPlatform.Snowflake] : []),
    ],
    environmentTypes: [EnvironmentDeploymentType.Production],
    resourceType: [
      'model',
      'source',
      'semantic_model',
      'exposure',
      'metric',
      'test',
      'seed',
      'macro',
      'snapshot',
      'saved_query',
    ],
    searchFields: allSearchTypes,
  };
};

export type FilterState = {
  filters: NodeFilters;
  activeFilterCount: number;
  clearFilters: () => void;
  updateFilters: (filter: NodeFilters, isAddOperation?: boolean) => void;
  resetToDefault: () => void;
};

const FilterContext = createContext<FilterState>({
  filters: {},
  activeFilterCount: 0,
  clearFilters: () => {},
  updateFilters: () => {},
  resetToDefault: () => {},
});

export const useFilterContext = () => {
  return useContext(FilterContext);
};

const logger = debug('dbt:Explorer:FilterProvider');

const safeJsonParse = (json: string | null): Record<string, any> | undefined => {
  if (!json) return undefined;
  try {
    return JSON.parse(json);
  } catch (e) {
    logger('Error parsing JSON', json, e);
    return undefined;
  }
};

const getFiltersFromParams = (searchParams: URLSearchParams) => {
  const filterParams = safeJsonParse(searchParams.get('filters'));
  const urlFilters = zNodeFilters.safeParse(filterParams);
  return {
    ...(urlFilters.success ? urlFilters.data : {}),
  } as NodeFilters;
};

export const FilterProvider: FC<PropsWithChildren<{}>> = ({ children }) => {
  const { hasAccountFeature } = useDbtCloudAccount();
  const [searchParams, setSearchParams] = useSearchParams();
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  const getUpdatedSearchParams = (
    searchParams: URLSearchParams,
    newFilters: NodeFilters,
  ) => {
    const filters = JSON.stringify(newFilters);
    if (filters === '{}') {
      searchParams.delete('filters');
    } else {
      searchParams.set('filters', filters);
    }
    return searchParams;
  };
  const [filters, setFilters] = useState<NodeFilters>(() =>
    getFiltersFromParams(searchParams),
  );

  const clearFilters = useCallback(() => {
    setSearchParamsRef.current((params) => {
      params.delete('filters');
      return params;
    });
  }, []);

  useEffect(() => {
    const newFilters = getFiltersFromParams(searchParams);
    setFilters(newFilters);
  }, [searchParams]);

  const addFilters = useCallback(
    (filterToAdd: NodeFilters) => {
      const newFilters = Object.entries(filterToAdd).reduce<Record<string, unknown>>(
        (acum, [key, value]) => {
          if (Array.isArray(value)) {
            acum[key] = [
              ...((acum[key] as string[] | undefined) ?? []),
              ...value,
            ].filter(unique);
          } else {
            acum[key] = value;
          }
          return acum;
        },
        { ...filters }, // initial value
      );
      setSearchParamsRef.current((searchParams) =>
        getUpdatedSearchParams(searchParams, newFilters),
      );
    },
    [filters],
  );

  const removeFilters = useCallback(
    (filterToRemove: NodeFilters) => {
      const newFilters = Object.entries(filterToRemove).reduce<Record<string, unknown>>(
        (acum, [key, value]) => {
          if (Array.isArray(value)) {
            const newValues = (acum[key] as string[] | undefined)
              ?.filter((item) => !(value as string[]).includes(item))
              ?.filter(unique);

            if (newValues?.length) {
              acum[key] = newValues;
            } else {
              delete acum[key];
            }
          } else {
            delete acum[key];
          }
          return acum;
        },
        { ...filters }, // initial value
      );

      setSearchParamsRef.current((searchParams) =>
        getUpdatedSearchParams(searchParams, newFilters),
      );
    },
    [filters],
  );

  const updateFilters = useCallback(
    (filterToUpdate: NodeFilters, isAddOperation = true) => {
      return isAddOperation
        ? addFilters(filterToUpdate)
        : removeFilters(filterToUpdate);
    },
    [addFilters, removeFilters],
  );

  const resetToDefault = useCallback(() => {
    setSearchParamsRef.current((prev) => {
      prev.set(
        'filters',
        JSON.stringify(getDefaultAccountFilters(hasAccountFeature('catalogIngestion'))),
      );
      return prev;
    });
  }, [hasAccountFeature]);

  const activeFilterCount = useMemo(() => {
    return Object.entries(filters).reduce((acum, [, value]) => {
      if (Array.isArray(value)) {
        return acum + value.length;
      } else if (value) {
        return acum + 1;
      }
      return acum;
    }, 0);
  }, [filters]);

  return (
    <FilterContext.Provider
      value={{
        filters,
        activeFilterCount,
        updateFilters,
        clearFilters,
        resetToDefault,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
};
